/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: =, Brian Bal, Ivan Ryabov, Leonid Khramov
 */
import React, { Component, useEffect, useState } from "react"
import { ApolloClient, InMemoryCache, ApolloProvider } from "@apollo/client"
import Bowser from "bowser"
import localForage from "localforage"
import { connect, useSelector, useDispatch } from "react-redux"
import { useLocation, useHistory } from "react-router"

import Toaster from "./components/Toaster"
import Session from "./modules/Session"
import GameListPage from "./modules/GameListPage"
import Rosters from "./modules/Rosters"
import { GamePage } from "./modules/GamePage"
import EventsPanel from "./modules/EventsPanel"
import PlayersPanel from "./modules/PlayersPanel"
import { ShareGame, SharePlayer } from "./modules/ShareDialog"
import MomentOptionsDialog from "./modules/MomentOptionsDialog"
import { SettingsModal } from "./components/SettingsModal"
import ChoosePlaylist from "./modules/ChoosePlaylist"
import PlayerDetailsPanel from "./modules/PlayerDetailsPanel"
import AddPlayer from "./modules/Rosters/containers/AddPlayer"
import EditPlayer from "./modules/Rosters/containers/EditPlayer"
import SwapPlayer from "./modules/Rosters/containers/SwapPlayer"
import SaveToProfile from "./modules/SaveToProfile"
import { UserProfileLinksDialog } from "./modules/UserProfile"
import UnsupportedBrowser from "./components/UnsupportedBrowser"
import Admin from "./modules/Admin"
import StartPage from "./modules/StartPage"
import Uploader from "./modules/Uploader/Uploader"
import QALinksResolverComponent from "./components/QALinksResolver"
import UpgradeToTraceTeamDialogComponent from "./components/UpgradeToTraceTeamDialogComponent"
import { webApi } from "./lib/WebApi"
import * as links from "./util/links"
import { TOUR_ROUTES, useABTests } from "./ABTests/helpers"
import {
    Element,
    BottomBanner,
    BottomToaster,
    Color,
    TrackingManager,
    startEventTracking,
    TrackingProvider,
    HashRouter,
    Route,
    useToken,
    DebugEnv,
    getSharedTrackingAppName,
} from "trux"
import { AdminInvites } from "./modules/Admin"
import { TRCCanvasComp } from "./trace/TRCCanvas"
//import { WelcomeToTeam } from "./modules/Family/WelcomeToTeam"
import InAppSurvey from "./modules/InAppSurvey"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { ReferralsLandingPage } from "./components/Referrals/ReferralsLandingPage"
import { UpgradeDialogComponent } from "./components/UpgradeDialogComponent"
import { UpgradeCompletionDialogComponent } from "./components/UpgradeCompletionDialogComponent"
import { ReferralsSuccessfulPage } from "./components/Referrals/ReferralsSuccessfulPage"
import { RemoveGoal } from "./modules/MomentEditor"
import DownloadPage from "./modules/DownloadPage"
import { EnjoyingMomentDialog } from "./modules/EnjoyingMomentDialog"
import { RosterInvite } from "./modules/Family/RosterInvite"
import { Family } from "./modules/Family"
import { RemoveFamily } from "./modules/Family/RemoveFamily"
import { EditFamilyMemberModal } from "./modules/Family/EditFamilyMemberModal"
import { InviteFamilyMemberModal } from "./modules/Family/InviteFamilyMemberModal"

import { useGetUserRelationsQuery } from "./generated/graphql"
import {
    LoadUser,
    LoadDivisions,
    LoadTeam,
    LoadGames,
    LoadGame,
    LoadPlayerStats,
    LoadRoster,
    LoadRosterRelations,
    LoadEvent,
    ForceGoodEvent,
    LoadHeatmap,
    LoadDivisionStaff,
    LoadTeamStaff,
    LoadStories,
} from "./modules/Loader"
import loadACL from "./modules/Loader/loadAcl"
import "./trace/grid.css"
import "./App.css"
import LoadChurnZero from "./modules/Loader/loadChurnZero"
import { parseQuery } from "./util/query"
import DownloadMomentDialog from "./modules/DownloadMomentDialog"
import { useFetchUserAndRelationsProfiles } from "./hooks/useFetchUserAndRelationsProfiles"

console.log(`Trace
If you are reading this, you are awesome!
\tWe are hiring:  https://www.traceup.com/careers?utm_campaign=awesomesauce
`)

startEventTracking()

localForage.config({
    name: "trace",
    version: 1.0,
})

const checkBrowser = () => {
    // Edge: 15+
    // FireFox: 57+
    // Chrome: 64+
    // Safari: 10+
    // iOS Safari: 11.2+
    // Chrome Android: 63+
    const supported = {
        msedge: ">=15",
        firefox: ">=57",
        safari: ">=10",
        ios: ">=10",
        chrome: ">=59",
        chromium: ">=59",
        samsung_internet: ">=9.2",
        uc: ">=12.12",
    }
    const browser = Bowser.getParser(window.navigator.userAgent)
    if (!browser.satisfies(supported)) {
        //return (<Redirect to={'/unsupported/browser'}/>)
    }

    return null
}

const getAndSaveShadowUser = (location) => {
    // If a uid is present, attempt to get a shadow user.
    const searchParams = parseQuery(location.search)
    if (searchParams && searchParams.uid) {
        const uid = searchParams.uid
        if (uid) {
            const splitUid = uid.split("-")
            const userId = splitUid[0]
            const hash = splitUid[1]
            localForage.setItem("shadowUserId", userId)
            localForage.setItem("shadowUserHash", hash)
        }
    }
}

const TeamsWebApp = (props) => {
    const dispatch = useDispatch()
    let settings = useSelector((state) => state.settings)
    let acl = useSelector((state) => state.acl.value)
    let event = useSelector((state) => state.video.value.source)
    let team = useSelector((state) => state.team.value)
    let user = useSelector((state) => state.user.value)
    let showBottomToast = useSelector((state) => state.bottomToast.show)
    let msgBottomToast = useSelector((state) => state.bottomToast.message)
    let linkBottomToast = useSelector((state) => state.bottomToast.link)
    let linkTextBottomToast = useSelector((state) => state.bottomToast.linkText)
    let linkStateBottomToast = useSelector(
        (state) => state.bottomToast.linkState
    )
    let playlistLinkBottomToast = useSelector(
        (state) => state.bottomToast.playlistLink
    )
    let isTeamLoggedIn = useSelector((state) => state.isTeamLoggedIn)
    const [createEnabled, setCreateEnabled] = useState(null)
    const [editEnabled, setEditEnabled] = useState(null)
    const token = useToken("App")
    useABTests()
    const location = useLocation()
    const history = useHistory()
    // eslint-disable-next-line eqeqeq
    const tokenReady = token != null

    const { data: relations } = useGetUserRelationsQuery({
        variables: {
            to_user_id: token?.user_id,
            token: token,
            include_self: true,
        },
        skip: !tokenReady,
    })

    useEffect(() => {
        dispatch({
            type: "COMMON_SET",
            key: "token",
            value: {
                loading: false,
                complete: !!token,
                value: token,
                error: null,
            },
            defaultValue: {
                loading: false,
                complete: false,
                error: null,
                value: null,
            },
        })
    }, [token])

    useEffect(() => {
        getAndSaveShadowUser(location)
    })

    useEffect(() => {
        if (acl && event) {
            setCreateEnabled(acl.create_moment_tool)
            setEditEnabled(event.meta.type !== "FullGameVideo")
        }
    }, [acl, event])

    useFetchUserAndRelationsProfiles(user, token)

    // Reload the acl whenever the team or user changes
    useEffect(() => {
        dispatch(loadACL())
    }, [team?.team_id, user?.user_id])

    const loadRateAppCounts = async () => {
        if (!settings.rateAppCountLoaded) {
            dispatch({
                type: "COMMON_SET",
                key: "settings",
                value: { rateAppCountLoaded: true },
            })

            let views = await localForage.getItem("rateapp-viewsSinceDialog")
            let lastShownAt = await localForage.getItem("rateapp-lastShownAt")
            if (views === null) {
                views = 0
            }
            if (lastShownAt === null) {
                lastShownAt = 0
            }
            console.log("loadRateAppCounts", "views", views)
            console.log("loadRateAppCounts", "lastShownAt", lastShownAt)
            dispatch({
                type: "COMMON_SET",
                key: "settings",
                value: { viewsSinceDialog: views, lastShownAt: lastShownAt },
            })
        }
    }

    useEffect(() => {
        loadRateAppCounts()
    }, [])

    const getShouldShowBottomBanner = (team) => {
        let shouldShow = false
        const contentAccess = team ? team.division?.content_access : true
        const teamViewAccess = team
            ? ["auto_allowed", "manual_allowed"].includes(contentAccess)
            : true
        shouldShow =
            tokenReady &&
            teamViewAccess &&
            !token?.user_id &&
            !window.location.href?.includes("cancel") &&
            !window.location.href?.includes("invite") &&
            !window.location.href?.includes("success") &&
            !window.location.href?.includes("session/login")
        return shouldShow
    }

    const handleBottomBannerButtonOnClick = (e) => {
        console.log("handleBottomBannerButtonOnClick")
        history.push(`/magic/session/login?redirect=${location.pathname}`)
    }

    const handleOnClearBottomToast = (e) => {
        dispatch({
            type: "BOTTOM_TOAST",
            message: "",
            show: false,
            playlistLink: null,
            link: null,
            linkText: null,
            linkState: null,
        })
    }

    const dismissSaveToProfile = (history) => {
        history.goBack()
    }

    const shouldShowBottomBanner = getShouldShowBottomBanner(team)

    getSharedTrackingAppName().appName = "GO"

    return (
        <div className="App">
            {checkBrowser(props)}
            <Route
                path="/:team_name/games/:game_id/playlists/:playlist_id/notes/:event_number"
                component={RedirectOldGameRoutes}
            />
            <Route path="/" component={TrackingManager} />
            <Route
                path="/"
                component={() => {
                    /*
                    document.dispatchEvent(
                        new CustomEvent("trc-track-page", {
                            detail: window.location.href,
                        })
                    )
                    */
                    return appPageChanged()
                }}
            />

            <Route
                exact
                path={links.QA_ROUTE}
                component={QALinksResolverComponent}
            />
            <Route
                exact
                path={links.QA_ROUTE_SUFFIX}
                component={QALinksResolverComponent}
            />

            <Route
                exact
                path="/unsupported/browser"
                component={UnsupportedBrowser}
            />

            <Route path="/test/debug" component={DebugEnv} />

            <Route path="/" component={LoadUser} />
            <Route path="/" component={LoadDivisions} />
            <Route path="/" component={LoadChurnZero} />
            <Route path="/:team_name" component={LoadTeam} />
            <Route path="/:team_name" component={LoadGames} />
            <Route exact path="/:team_name" component={LoadStories} />
            <Route path="/:team_name/:page/:game_id" component={LoadGame} />
            <Route
                path="/:team_name/games/:game_id"
                component={LoadPlayerStats}
            />
            <Route
                path="/:team_name/:page/:game_id/:playlist_id/:event_number"
                component={LoadEvent}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                component={ForceGoodEvent}
            />

            <Route path="/:team_name/rosters" component={LoadRoster} />
            <Route
                path="/:team_name/rosters/:roster_type/:roster_id/players/:player_id"
                component={LoadRosterRelations}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster"
                component={LoadRoster}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster/:action/:player_id"
                component={LoadRosterRelations}
            />

            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number"
                component={LoadHeatmap}
            />
            <Route
                path="/:team_name/settings/division/:division_id"
                component={LoadDivisionStaff}
            />
            <Route
                path="/:team_name/settings/division/:division_id/teams/:team_id"
                component={LoadTeamStaff}
            />

            <Session />
            <Route
                exact
                path={[
                    "/",
                    "/go(.+)/upgrade",
                    "/go(.+)/upgrade/success/*",
                    "/go(.+)/upgrade/cancel",
                ]}
                component={StartPage}
            />

            <Route
                exact
                path={[
                    "/ref/qr/:user_id",
                    "/ref/qr",
                    "/ref/tripod",
                    "/ref/sms/:user_id",
                    "/ref/sms",
                ]}
                component={ReferralsLandingPage}
            />
            <Route
                exact
                path="/ref/referral-success"
                component={ReferralsSuccessfulPage}
            />
            <ProtectedRoute
                exact
                path={[
                    "/:team_name",
                    "/:team_name/upgrade-trace-team",
                    "/:team_name/go(.+)/",
                    "/:team_name/go(.+)/upgrade",
                    "/:team_name/go(.+)/upgrade/cancel",
                    "/:team_name/go(.+)/upgrade/success/*",
                ]}
                component={GameListPage}
                isLoggedIn={token?.user_id}
                hasPermissions={true}
                permissionsLoaded={token !== null}
                dismissLogin={(history) => history.goBack()}
            />
            <ProtectedRoute
                path="/:team_name/welcome"
                component={GameListPage}
                isLoggedIn={token?.user_id}
                hasPermissions={true}
                permissionsLoaded={token !== null}
                dismissLogin={(history) => history.goBack()}
            />

            <Route
                path="/:team_name/welcome"
                render={(props) => (
                    <RosterInvite
                        {...props}
                        isLoggedIn={token?.user_id}
                        token={token}
                    />
                )}
            />
            <Route
                path="/:team_name/welcome/:user_id"
                render={(props) => (
                    <RosterInvite
                        {...props}
                        isLoggedIn={token?.user_id}
                        token={token}
                    />
                )}
            />
            <Route path="/:team_name/rosters" component={Rosters} />

            <ProtectedRoute
                path="/:team/family"
                isLoggedIn={token?.user_id}
                hasPermissions={true}
                permissionsLoaded={token !== null}
                dismissLogin={(history) => history.goBack()}
                component={Family}
            />
            <Route
                path="/:team/family/:user_id/remove"
                component={RemoveFamily}
            />
            <Route
                path="/:team/family/:user_id/edit"
                component={EditFamilyMemberModal}
            />
            <Route
                path="/:team/family/invite"
                component={InviteFamilyMemberModal}
            />

            <Route
                path="/magic/:user/:token/:team_name/welcome"
                component={InviteMagic}
            />

            <Route
                path="/magic/:user/:token/:team_id/games/:game_id"
                component={GameMagic}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                render={(props) => (
                    <GamePage
                        {...props}
                        token={token}
                        createEnabled={createEnabled}
                        editEnabled={editEnabled}
                    />
                )}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                render={(props) => <InAppSurvey {...props} token={token} />}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/enjoy"
                component={EnjoyingMomentDialog}
            />
            <Route
                path="/:team_name/games/:game_id/download-only"
                component={DownloadPage}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                component={EventsPanel}
            />
            <Route
                exact
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players"
                component={PlayersPanel}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number"
                component={PlayerDetailsPanel}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number"
                component={SharePlayer}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster/edit/:player_id"
                component={EditPlayer}
            />
            <Route
                exact
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster/swap/:player_id"
                component={SwapPlayer}
            />
            <Route
                exact
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster/swap/:player_id/:to_trace_number"
                component={SwapPlayer}
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/players/:trace_number/roster/add"
                component={AddPlayer}
                isLoggedIn={token?.user_id}
                hasPermissions={isTeamLoggedIn}
                permissionsLoaded={token !== null}
                dismissLogin={(history) => history.goBack()}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/share"
                component={ShareGame}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/settings"
                component={SettingsModal}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                component={ChoosePlaylist}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                render={(props) => (
                    <SaveToProfile
                        {...props}
                        relations={relations?.userRelations}
                        dismissLogin={dismissSaveToProfile}
                    />
                )}
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/download-options"
                component={DownloadMomentDialog}
                isLoggedIn={token?.user_id}
                hasPermissions={token?.user_id}
                permissionsLoaded={token !== null}
                dismissLogin={(history) =>
                    history.push(
                        history.location.pathname.replace(
                            "/download-options",
                            "/options"
                        )
                    )
                }
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/download-share"
                component={DownloadMomentDialog}
                isLoggedIn={token?.user_id}
                hasPermissions={token?.user_id}
                permissionsLoaded={token !== null}
                dismissLogin={(history) =>
                    history.push(
                        history.location.pathname.replace(
                            "/download-share",
                            "/share"
                        )
                    )
                }
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                component={MomentOptionsDialog}
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number"
                component={UserProfileLinksDialog}
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/do/remove-goal"
                component={RemoveGoal}
                isLoggedIn={token?.user_id}
                hasPermissions={createEnabled}
                permissionsLoaded={createEnabled !== null}
                dismissLogin={(history) =>
                    history.push(
                        history.location.pathname.replace(
                            "/do/remove-goal",
                            "/options"
                        )
                    )
                }
            />
            <Route path="/:team/settings" component={Admin} />
            <Route path="/invite" component={AdminInvites} />
            <Route path="/:team/game-editor" component={Uploader} />

            <Route
                path="/:team/version"
                component={() => {
                    return <pre>version: {process.env.REACT_APP_VERSION}</pre>
                }}
            />

            <Route path="/:team/trc-canvas" component={TRCCanvasComp} />

            <Route path={TOUR_ROUTES} component={Chameleon} />
            <Route
                path={"/*/upgrade-trace-team"}
                component={UpgradeToTraceTeamDialogComponent}
            />
            <Route
                exact
                path={"*go(.+)/upgrade"}
                component={UpgradeDialogComponent}
            />
            <Route
                path={["/*/go(.+)/success", "/*/go(.+)/upgrade/cancel"]}
                render={(props) => (
                    <UpgradeCompletionDialogComponent
                        {...props}
                        upgradedTo="traceId"
                        showButton={true}
                        onClose={(history, location) => {
                            history.replace("../../")
                        }}
                        onAction={(userId) => {
                            let newPath = links.traceIdUrl(
                                `/find/user/${userId}`
                            )
                            window.location.href = newPath
                        }}
                    />
                )}
            />
            <Route
                path={["/*/success/traceId"]}
                render={(props) => (
                    <UpgradeCompletionDialogComponent
                        {...props}
                        upgradedTo="traceId"
                        showButton={true}
                        buttonTitle="Go to Trace iD"
                        onClose={(history, location) => {
                            let newPath = location.pathname.split("success")[0]
                            history.push(newPath)
                        }}
                        onAction={(userId) => {
                            let newPath = links.traceIdUrl(
                                `/find/user/${userId}`
                            )
                            window.location.href = newPath
                        }}
                    />
                )}
            />
            <Route
                path={"/*/success/family"}
                render={(props) => (
                    <UpgradeCompletionDialogComponent
                        {...props}
                        showButton={false}
                        upgradedTo="family"
                        showConfirmationText={true}
                        hideCloseButton={true}
                    />
                )}
            />
            <Route
                path={"/*/success/team"}
                render={(props) => (
                    <UpgradeCompletionDialogComponent
                        {...props}
                        showButton={false}
                        upgradedTo="team"
                        showConfirmationText={true}
                        hideCloseButton={true}
                    />
                )}
            />
            <Toaster />
            <Route
                path="/"
                render={(props) => (
                    <BottomToaster
                        show={showBottomToast}
                        history={history}
                        message={msgBottomToast}
                        link={linkBottomToast}
                        playlistLink={playlistLinkBottomToast}
                        linkText={linkTextBottomToast}
                        linkState={linkStateBottomToast}
                        onClearMessage={(e) => handleOnClearBottomToast()}
                    />
                )}
            />
            {shouldShowBottomBanner && (
                <BottomBanner
                    labelColor={Color.White2}
                    labelMessage={"Get more out of Trace"}
                    buttonClass={"banner-button"}
                    buttonContent={"Sign in"}
                    bannerColor={"color-trace-shade"}
                    buttonAction={(e) => handleBottomBannerButtonOnClick(e)}
                />
            )}
            <div id="portal"></div>
            {process.env.REACT_APP_KB_BUILD && (
                <Element
                    tag="a"
                    target="_blank:"
                    href={process.env.REACT_APP_GO_TRACEUP + "/notes.html"}
                    className="build-banner"
                >
                    {process.env.REACT_APP_KB_BUILD_NAME}
                </Element>
            )}
        </div>
    )
}

const cache = new InMemoryCache({
    typePolicies: {
        profile: {
            keyFields: ["hash_key"],
        },
        collection: {
            keyFields: ["hash_key"],
        },
        collectionItem: {
            keyFields: ["user_playlist_clip_id"],
        },
        userRelations: {
            keyArgs: ["to_user_id", "include_self"],
        },
        notificationMessage: {
            keyFields: ["msg_id"],
        },
        collections: {
            keyFields: ["user_id"],
        },
        contentAccessForTeam: {
            keyFields: ["team_id"],
        },
        user: {
            keyFields: ["user_id"],
        },
        fullVideoByMoment: {
            keyArgs: ["game_clip_id"],
        },
        fullVideoMoment: {
            keyArgs: ["collection_hash_key", "user_playlist_clip_id"],
        },
        latestNotifications: {
            keyArgs: ["msg_id"],
        },
        momentCollectionInfo: {
            keyArgs: ["user_id", "game_clip_id"],
        },
        momentCollectionsByUserAndGame: {
            keyArgs: ["user_id", "game_id"],
        },
        userNames: {
            keyArgs: ["user_id"],
        },
        surveys: {
            keyArgs: ["client_id"],
        },
    },
})

const client = new ApolloClient({
    uri: process.env.REACT_APP_GRAPHQL_API,
    cache: cache,
})

export class App extends Component {
    render() {
        let opts = {
            urlBase: "https://ed210.go.traceup.com",
            trackerUrl: "https://ed210.go.traceup.com/ed210.php",
            srcUrl: "https://ed210.go.traceup.com/ed210.js",
            disabled: false,
            heartBeat: {
                active: true,
                seconds: 10,
            },
            linkTracking: true,
            configurations: {
                disableCookies: true,
                setSecureCookie: true,
                setRequestMethod: "POST",
                alwaysUseSendBeacon: false,
                requireConsent: true,
                setConsentGiven: true,
            },
        }
        return (
            <TrackingProvider siteId={1} opts={opts}>
                <ApolloProvider client={client}>
                    <HashRouter>
                        <TeamsWebApp />
                    </HashRouter>
                </ApolloProvider>
            </TrackingProvider>
        )
    }
}

const setupChameleon = () => {
    let tag = document.createElement("script")
    tag.type = "text/javascript"
    tag.innerText = `
/* Chameleon - better user onboarding */!function(t,n,o){var a="chmln",e="adminPreview",c="setup identify alias track clear set show on off custom help _data".split(" ");if(n[a]||(n[a]={}),n[a][e]&&(n[a][e]=!1),!n[a].root){n[a].accountToken=o,n[a].location=n.location.href.toString(),n[a].now=new Date;for(var s=0;s<c.length;s++)!function(){var t=n[a][c[s]+"_a"]=[];n[a][c[s]]=function(){t.push(arguments)}}();var i=t.createElement("script");i.src="https://fast.trychameleon.com/messo/"+o+"/messo.min.js",i.async=!0,t.head.appendChild(i)}}(document,window,"SJQkNaNGZUQFvu65qls0YwA79l9DhnoVFDocE9mYyEa3AV-1HV78s-By0VuSBploLtLBJm");
    `
    document.head.appendChild(tag)
}

const Chameleon = (props) => {
    let user = useSelector((state) => state.user.value)

    if (!window.chmlnSetup) {
        window.chmlnSetup = true
        setupChameleon()

        setTimeout(() => {
            let now = Date.now()
            let userId = now
            let email = `${now}@visitor.traceup.com`
            if (user) {
                userId = user.user_id
                email = user.email
            }
            console.log("chameleon user", userId, email)

            try {
                window.chmln.identify(userId, {
                    visitor: true,
                })
                let tourId = "5f314ae61886640013009935"
                if (window.innerWidth > 416) {
                    tourId = "5f3bfb82382540000aeb15d5"
                }
                window.chmln.show(tourId)
            } catch (e) {
                console.error("Chameleon", e)
            }
        }, 2000)
    }

    return null
}

const RedirectOldGameRoutes = (props) => {
    let from = window.location.hash.replace(/^#/gi, "")

    let to = from.replace(`${props.match.params.team_name}-`, "")
    to = to.replace("/playlists", "")
    to = to.replace(/all-notes$/, "momments")
    to = to.replace("/notes", "")

    console.log("redirectOldGameRoutes", "from", from, "to", to)

    window.location.href = "/#" + to
    window.location.reload()

    return null
}

const mapStateToProps = (state) => {
    return {}
}

const mapDispatchToProps = (dispatch) => {
    return {
        setWindowSize: (w, h) => {
            dispatch({ type: "WINDOW_SIZE", height: h, width: w })
        },
        fromIOS: () => {
            dispatch({
                type: "COMMON_SET",
                key: "via",
                value: "iosapp",
            })
        },
    }
}

function InviteMagicComp(props) {
    props.fromIOS()
    console.log("InviteMagic", props.history)

    webApi.loginByCode(props.match.params.user, props.match.params.token)

    props.history.replace(`/${props.match.params.team_name}/welcome`)
    return null
}

const InviteMagic = connect(
    mapStateToProps,
    mapDispatchToProps
)(InviteMagicComp)

function GameMagicComp(props) {
    props.fromIOS()
    console.log("GameMagic", props.history)

    let gameIdParts = props.match.params.game_id.split("-")
    let GameId = 0
    if (gameIdParts.length > 0) {
        GameId = gameIdParts[1]
    }

    webApi.loginByCode(props.match.params.user, props.match.params.token)

    props.history.replace(
        links.gameUrl(props.match.params.team_id, "touch-chains", GameId)
    )

    return null
}

const GameMagic = connect(mapStateToProps, mapDispatchToProps)(GameMagicComp)

const appPageChanged = (props) => {
    let hashUrl = window.location.hash.replace("#/", "")
    if (hashUrl.includes("/games/")) {
        //countPageView(hashUrl)
        // update to old version urls for stats
        let hashParts = hashUrl.split("/")
        let tmid = hashParts[0]
        let gmid = hashParts[2]
        hashParts[2] = `${tmid}-${gmid}`
        hashParts.splice(3, 0, "playlists")
        hashParts.splice(5, 0, "notes")
        hashUrl = hashParts.join("/")
    }
    hashUrl = "#/" + hashUrl

    //statPageView(hashUrl)
    //statEvent("page", "load", "version", process.env.REACT_APP_VERSION)

    return null
}
